import type { AssetFileDoc, AssetFolderDoc } from '@bb/domain/dist/schemas';
import { Card, List, Pagination } from 'components';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { AssetExplorerView } from '..';
import AssetListItem from './AssetListItem';
import AssetGridItem from './AssetGridItem';

type AssetExplorerListProps = {
  actionsEnabled?: boolean;
  explorerView: AssetExplorerView;
  assets: (AssetFolderDoc | AssetFileDoc)[];
  currPage: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onAssetClick: (
    asset: AssetFolderDoc | AssetFileDoc,
    filetype: string,
  ) => void;
  onActionClick: (asset: AssetFolderDoc | AssetFileDoc) => void;
  pageSize: number;
  totalAssets: number;
};

const AssetExplorerList: React.FC<AssetExplorerListProps> = ({
  actionsEnabled = true,
  explorerView,
  assets,
  currPage,
  loading = false,
  onPageChange,
  onAssetClick,
  onActionClick,
  pageSize,
  totalAssets,
}) => {
  // show list view when no assets to re-use lists empty data message
  const isGridView = useMemo(
    () => explorerView === 'grid' && assets?.length > 0,
    [explorerView, assets?.length],
  );

  const gridLayout = useMemo(() => {
    if (!isGridView) return undefined;
    return { column: 4, gutter: 8 };
  }, [isGridView]);

  return (
    <>
      <StyledCard $isGridView={isGridView}>
        <List
          data-cy="asset-explorer-list"
          size="small"
          split
          loading={loading}
          itemLayout="horizontal"
          dataSource={assets}
          grid={gridLayout}
          renderItem={(item: AssetFolderDoc | AssetFileDoc) =>
            explorerView === 'grid' ? (
              <AssetGridItem
                item={item}
                actionsEnabled={actionsEnabled}
                onAssetClick={onAssetClick}
                onActionClick={onActionClick}
              />
            ) : (
              <AssetListItem
                item={item}
                actionsEnabled={actionsEnabled}
                onAssetClick={onAssetClick}
                onActionClick={onActionClick}
              />
            )
          }
        />
      </StyledCard>
      <StyledPagination
        pageSize={pageSize}
        total={totalAssets}
        current={currPage}
        onChange={onPageChange}
        showSizeChanger={false}
      />
    </>
  );
};

export default AssetExplorerList;

const StyledCard = styled(Card)<{ $isGridView: boolean }>`
  && {
    padding: 0;
    ${props => props.$isGridView && `background: transparent;`}
    ${props => props.$isGridView && `padding-right: 8px;`}
  }

  && {
    .ant-card-body {
      padding: 0;
    }

    .ant-list .ant-list-items,
    .ant-list ul {
      margin-bottom: 0;
    }
  }
`;

const StyledPagination = styled(Pagination)`
  padding: 0 16px;
  text-align: left;
`;
